import type { FC, SVGProps } from 'react';
import type { UnitHeaderBadgeColors } from '../../../businessLogicService/StudentDashboardAux/getColorByUnit';

interface Props extends SVGProps<SVGSVGElement> {
  colors: UnitHeaderBadgeColors;
}

// The Foundations badge: an arch with a shaded 3-book stack and a sparkle, tinted with the selected
// unit's colors. Each book is a lighter "face" path over a darker "spine" path (masked to rounded
// rectangles) to keep the 3D relief. The inner disc/shadow/border take the unit's lighter shades.
export const UnitHeaderFoundationsIcon: FC<Props> = ({ colors, ...svgProps }) => {
  // viewBox padded to height 72 (arch is 60) so the arch renders at the same size as the
  // support/target circles, whose 55x55 disc sits in a 55x66 viewBox with room for the bubble.
  return (
    <svg viewBox="0 0 56 72" fill="none" xmlns="http://www.w3.org/2000/svg" {...svgProps}>
      <g clipPath="url(#clip0_13542_24650)">
        <path
          className={colors.archBackground}
          d="M0 28C0 12.536 12.536 0 28 0C43.464 0 56 12.536 56 28V55.5556C56 58.0102 54.0102 60 51.5556 60H4.44444C1.98985 60 0 58.0102 0 55.5556V28Z"
        />
        <ellipse className={colors.archShadow} cx="28.0069" cy="50.3275" rx="28.1202" ry="10.68" />
        <mask
          id="mask0_13542_24650"
          style={{ maskType: 'alpha' }}
          maskUnits="userSpaceOnUse"
          x="13"
          y="39"
          width="27"
          height="9"
        >
          <path
            d="M13.4395 40.166C13.4395 39.5493 13.92 39.0493 14.5128 39.0493H38.1264C38.7192 39.0493 39.1998 39.5493 39.1998 40.166V46.8659C39.1998 47.4826 38.7192 47.9826 38.1264 47.9826H14.5128C13.92 47.9826 13.4395 47.4826 13.4395 46.8659V40.166Z"
            fill="white"
          />
        </mask>
        <g mask="url(#mask0_13542_24650)">
          <path className={colors.bookFace} d="M13.4395 39.0493H33.8331V47.9826H13.4395V39.0493Z" />
          <path className={colors.bookSpine} d="M33.834 39.0493H39.2007V47.9826H33.834V39.0493Z" />
        </g>
        <mask
          id="mask1_13542_24650"
          style={{ maskType: 'alpha' }}
          maskUnits="userSpaceOnUse"
          x="11"
          y="21"
          width="27"
          height="10"
        >
          <path
            d="M11.293 22.2993C11.293 21.6826 11.7735 21.1826 12.3663 21.1826H35.98C36.5728 21.1826 37.0533 21.6826 37.0533 22.2993V28.9992C37.0533 29.6159 36.5728 30.1159 35.98 30.1159H12.3663C11.7735 30.1159 11.293 29.6159 11.293 28.9992V22.2993Z"
            fill="white"
          />
        </mask>
        <g mask="url(#mask1_13542_24650)">
          <path className={colors.bookFace} d="M11.293 21.1826H31.6866V30.1159H11.293V21.1826Z" />
          <path className={colors.bookSpine} d="M31.6875 21.1826H37.0542V30.1159H31.6875V21.1826Z" />
        </g>
        <mask
          id="mask2_13542_24650"
          style={{ maskType: 'alpha' }}
          maskUnits="userSpaceOnUse"
          x="18"
          y="30"
          width="27"
          height="10"
        >
          <path
            d="M18.8066 31.2329C18.8066 30.6162 19.2872 30.1162 19.88 30.1162H43.4936C44.0864 30.1162 44.567 30.6162 44.567 31.2329V37.9328C44.567 38.5495 44.0864 39.0495 43.4936 39.0495H19.88C19.2872 39.0495 18.8066 38.5495 18.8066 37.9328V31.2329Z"
            fill="white"
          />
        </mask>
        <g mask="url(#mask2_13542_24650)">
          <path className={colors.bookFace} d="M18.8066 30.1162H39.2002V39.0495H18.8066V30.1162Z" />
          <path className={colors.bookSpine} d="M39.2012 30.1162H44.5679V39.0495H39.2012V30.1162Z" />
        </g>
        <path
          className={colors.sparkle ?? colors.bookFace}
          d="M42.1199 17.833L42.9083 20.0971L45.0387 20.9348L42.9083 21.7726L42.1199 24.0367L41.3316 21.7726L39.2012 20.9348L41.3316 20.0971L42.1199 17.833Z"
        />
      </g>
      <path
        className={colors.archBorder}
        d="M28 2C42.3594 2 54 13.6406 54 28V55.5557C53.9999 56.9056 52.9056 57.9999 51.5557 58H4.44434C3.09439 57.9999 2.00006 56.9056 2 55.5557V28C2 13.6406 13.6406 2 28 2Z"
        strokeWidth="4"
        strokeLinejoin="round"
      />
      <defs>
        <clipPath id="clip0_13542_24650">
          <path
            d="M0 28C0 12.536 12.536 0 28 0C43.464 0 56 12.536 56 28V55.5556C56 58.0102 54.0102 60 51.5556 60H4.44444C1.98985 60 0 58.0102 0 55.5556V28Z"
            fill="white"
          />
        </clipPath>
      </defs>
    </svg>
  );
};
